import toast from 'react-hot-toast';

/**
 * Extracts a human-readable message from an API error.
 *
 * The custom fetch client (src/api/client.ts) throws a flat object shaped like
 * `{ status, statusText, data, message, url }`, where `data` is the parsed
 * NestJS body `{ statusCode, message, error }` and `message` can be a string
 * or string[]. Older axios-style code expected `error.response.data.message`,
 * so we check every shape and fall back to a provided default.
 */
export function getApiErrorMessage(error: any, fallback: string): string {
  const raw =
    error?.data?.message ??
    error?.response?.data?.message ??
    error?.data?.error ??
    (error instanceof Error ? error.message : undefined);

  if (Array.isArray(raw)) {
    const joined = raw.filter(Boolean).join(', ');
    if (joined) return joined;
  } else if (typeof raw === 'string' && raw.trim()) {
    // The client sets a generic "Request failed with status code N" message;
    // prefer the server-provided detail, otherwise the caller's fallback.
    if (!/^Request failed with status code/i.test(raw)) return raw;
  }

  return fallback;
}

export function handleApiError(error: any, context: string) {
  if (error?.response?.data?.message || error?.data?.message) {
    const errorMessage = error?.response?.data?.message || error?.data?.message;
    
    // Check for specific error messages like clutch errors
    if (Array.isArray(errorMessage) && errorMessage.some(msg => msg.includes('property date should not exist'))) {
      // Specific breeding clutch error
      toast.error('Failed to add clutch: backend validation error. Try refreshing the page.');
      console.error(`API Error: ${context}`, error);
      return;
    }
    
    // Regular error handling
    const message = Array.isArray(errorMessage) ? errorMessage.join(', ') : errorMessage;
    toast.error(`${context}: ${message}`);
    console.error(`API Error: ${context}`, error);
  } else if (error?.message) {
    toast.error(`${context}: ${error.message}`);
    console.error(`API Error: ${context}`, error);
  } else {
    toast.error(`Unknown Error: ${context}`);
    console.error(`Unknown Error: ${context}`, error);
  }
} 